<template>
	<div
		ref="container"
		class="highlight-animation"
	>
		<div v-if="staticGraphic?.imageUrl">
			<img
				:src="$Helpers.getScaledImageUrl(staticGraphic.imageUrl, 1080)"
			>
		</div>
		<div v-if="shouldShowGraphic">
			<iframe
				v-if="currentGraphic"
				:key="currentClip?.tag?.id"
				:src="delayedGraphicUrl"
				:style="scaleStyle"
				class="highlight-animation__iframe"
				frameborder="0"
				@load="handleIframeLoad"
			/>
			<!-- Hidden iframes for preloading -->
			<div class="highlight-animation__preload">
				<iframe
					v-for="url in preloadUrls"
					:key="url"
					:src="url"
					style="display: none;"
					frameborder="0"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import ApiClient from '@videotools/js/api';
import laroute from '@laroute';
import { GraphicAspectRatio } from '@shared/enums/GraphicAspectRatio';
import GraphicsMixin from '@shared/js/mixins/GraphicsMixin';

export default {
	name:   'HighlightAnimation',
	mixins: [GraphicsMixin],
	props:  {
		currentClip: {
			type:    Object,
			default: null,
		},
		selectedClips: {
			type:    Array,
			default: () => [],
		},
		eventLabels: {
			type:    Array,
			default: () => [],
		},
		isPreviewingTag: {
			type:    Boolean,
			default: false,
		},
		eventId: {
			type:     Number,
			required: true,
		},
		enabledGraphicIds: {
			type:    Array,
			default: () => [],
		},
		isPlaying: {
			type:     Boolean,
			required: true,
		},
		currentProgress: {
			type:     Number,
			required: true,
		},
	},
	data() {
		return {
			preloadUrls:        [],
			scale:              1,
			resizeObserver:     null,
			graphicsConfig:     [],
			delayedGraphicUrl:  null,
			isConfigLoaded:     false,
			currentIframe:      null,
			isClipTransition:   false,
			showGraphicTimeout: null,
			isIframeReady:      false,
		};
	},
	computed: {
		scaleStyle() {
			return {
				transform: `scale(${this.scale})`,
			};
		},
		// Calculate the relative progress within the current clip
		clipProgress() {
			if (!this.currentClip?.tag) {
				return 0;
			}

			const clipStart = this.currentClip.tag.timestamp - this.currentClip.tag.secondsBefore;
			return Math.max(0, this.currentProgress - clipStart);
		},
		currentGraphic() {
			return this.findGraphicConfigForTag(this.currentClip?.tag);
		},
		shouldShowGraphic() {
			// Show non-animated graphics instantly because they are static images
			return this.delayedGraphicUrl && !this.isPreviewingTag;
		},
		staticGraphic() {
			return this.graphicsConfig.find(graphic => this.enabledGraphicIds.includes(graphic.id) && !graphic.isAnimated && graphic.imageUrl);
		},
	},
	watch: {
		selectedClips: {
			immediate: true,
			async handler(newClips) {
				// Only update if we have the config
				if (this.isConfigLoaded) {
					await this.updatePreloadUrls(newClips);

					// Handle the first clip if it's the current clip
					if (newClips.length > 0 && this.currentClip?.id === newClips[0].id) {
						await this.loadGraphicForTag(newClips[0].tag);
					}
				}
			},
		},
		currentClip: {
			immediate: true,
			async handler(newClip, oldClip) {
				if (newClip?.tag?.id !== oldClip?.tag?.id && this.isConfigLoaded) {
					this.isClipTransition = true;
					this.isIframeReady = false;

					// Clear any existing timeout
					if (this.showGraphicTimeout) {
						clearTimeout(this.showGraphicTimeout);
					}

					// Hide current graphic immediately
					this.delayedGraphicUrl = null;

					await this.updatePreloadUrls(this.selectedClips);

					if (newClip?.tag) {
						await this.loadGraphicForTag(newClip.tag);
					}
				}
			},
		},
		// Add watcher for enabledGraphicIds to reload current graphic when selections change
		enabledGraphicIds: {
			async handler() {
				if (this.isConfigLoaded) {
					// For each enabled graphic, check if it needs an ad server image and load it
					for (const graphicId of this.enabledGraphicIds) {
						const graphic = this.graphicsConfig.find(g => g.id === graphicId);

						if (graphic.adButlerConfig) {
							graphic.imageUrl = await this.getAdServerGraphicImage(
								graphicId,
								graphic.adButlerConfig.zoneId,
								graphic.adButlerConfig.format,
								graphic.adButlerConfig.keywords,
							);
						}
					}

					if (this.currentClip?.tag) {
						await this.loadGraphicForTag(this.currentClip.tag);
					}
				}
			},
		},
		// Add watcher for graphicsConfig
		isConfigLoaded: {
			immediate: true,
			async handler(isLoaded) {
				if (isLoaded) {
					// Once config is loaded, update graphics for current state
					await this.updatePreloadUrls(this.selectedClips);
					if (this.currentClip?.tag) {
						await this.loadGraphicForTag(this.currentClip.tag);
					}
				}
			},
		},
		// Watch for playback state changes
		isPlaying(playing) {
			this.controlAnimation(playing ? 'play' : 'pause');
		},

		// Watch for progress changes to handle seeking
		clipProgress(newProgress, oldProgress) {
			// Skip seek operations during clip transitions
			if (this.isClipTransition) {
				return;
			}

			// If the progress jumped significantly, it's likely a seek operation
			if (Math.abs(newProgress - oldProgress) > 1) {
				this.controlAnimation('seek', newProgress * 1000); // Convert to milliseconds
			}
		},

		// When current clip changes, restart the animation
		'currentClip.tag.id'() {
			this.isClipTransition = true;
			// Add a small delay to ensure the transition flag is respected
			this.$nextTick(() => {
				this.controlAnimation('restart');
				// Reset the transition flag after a short delay to allow for the restart
				setTimeout(() => {
					this.isClipTransition = false;
				}, 100);
			});
		},
	},
	mounted() {
		this.loadGraphicsOverlaysConfig();
		this.updateScale();

		// Set up ResizeObserver
		if (window.ResizeObserver && this.$refs.container) {
			this.resizeObserver = new ResizeObserver(this.updateScale);
			this.resizeObserver.observe(this.$refs.container);
		}
	},
	beforeDestroy() {
		if (this.showGraphicTimeout) {
			clearTimeout(this.showGraphicTimeout);
		}
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
		}
	},
	methods: {
		async updatePreloadUrls(clips) {
			if (!clips.length) {
				this.preloadUrls = [];
				return;
			}

			// Find current clip index
			const currentIndex = clips.findIndex(clip => clip.id === this.currentClip?.id);

			// Get next 2 clips for preloading (if they exist)
			const preloadClips = clips.slice(currentIndex + 1, currentIndex + 3);

			// Get their graphic URLs
			const urls = [];
			for (const clip of preloadClips) {
				const graphicUrl = await this.getGraphicUrlForTag(clip.tag);
				if (graphicUrl) {
					urls.push(graphicUrl);
				}
			}
			this.preloadUrls = urls;
		},
		async loadGraphicsOverlaysConfig() {
			try {
				const graphicSets = await ApiClient.getGraphicSets();

				// Map the new graphicSets structure to match the old graphicsConfig structure
				const graphicsConfig = [];
				graphicSets.forEach(set => {
					set.graphics.forEach(graphic => {
						if (graphic.aspectRatio !== GraphicAspectRatio.AR_16_9) {
							return; // Only inlcude valid 16/9 images
						}

						const config = {
							...graphic,
							name: graphic.name ?? set.name,
						};

						if (set.isAdButlerGraphic) {
							config.adButlerConfig = {
								zoneId:   set.adButlerZoneId,
								keywords: set.adButlerSendLabels ? this.eventLabels : [],
								format:   graphic.adButlerFormat,
							};
						}

						graphicsConfig.push(config);
					});
				});

				this.graphicsConfig = graphicsConfig;
				this.isConfigLoaded = true;

				// Emit the config array for parent to handle graphics selection
				const configForSelection = graphicsConfig.map(config => ({
					id:      config.id,
					name:    config.name,
					group:   config.isAnimated ? 'animation' : 'static',
					tagIds:  config.tags.map(tag => tag.id),
					allTags: config.allTags,
				}));
				this.$emit('graphics-config-loaded', configForSelection);
			} catch (error) {
				console.error('Failed to load graphics config:', error);
				this.graphicsConfig = [];
				this.isConfigLoaded = false;
			}
		},
		async getGraphicUrlForTag(tag) {
			const graphicConfig = this.findGraphicConfigForTag(tag);
			if (!graphicConfig) {
				return null;
			}

			return await this.loadTagGraphic(graphicConfig, tag);
		},
		async loadTagGraphic(graphicConfig, tag) {
			try {
				const params = {
					graphic:    graphicConfig.id,
					eventId:    this.eventId,
					eventTagId: tag.id,
					loop:       false,
					autoplay:   true,
				};
				return laroute.route('api.graphics.view', params);
			} catch (error) {
				console.error('Failed to load tag graphic:', error);
				return null;
			}
		},
		async loadGraphicForTag(tag) {
			const newUrl = await this.getGraphicUrlForTag(tag);

			// Clear any existing timeout
			if (this.showGraphicTimeout) {
				clearTimeout(this.showGraphicTimeout);
			}

			// Only proceed if we have a valid URL
			if (newUrl) {
				// Delay showing the new graphic
				this.showGraphicTimeout = setTimeout(() => {
					this.delayedGraphicUrl = newUrl;
				}, 500);
			} else {
				this.delayedGraphicUrl = null;
			}
		},
		findGraphicConfigForTag(tag) {
			// Only look for enabled event_tag graphics
			return this.graphicsConfig.find(graphic => this.enabledGraphicIds.includes(graphic.id) && graphic.isAnimated && (graphic.allTags || graphic.tags.some(graphicTag => graphicTag.name === tag.name)));
		},
		updateScale() {
			if (!this.$refs.container) {
				return;
			}

			const container = this.$refs.container;
			const containerWidth = container.clientWidth;
			const containerHeight = container.clientHeight;

			// Calculate scale based on container dimensions
			const scaleX = containerWidth / 1920;
			const scaleY = containerHeight / 1080;
			this.scale = Math.min(scaleX, scaleY);
		},
		controlAnimation(command, value = null) {
			// Only try to control animation if iframe is ready
			if (!this.currentIframe || !this.isIframeReady) {
				return;
			}

			const message = { command };
			if (value !== null) {
				message.value = value;
			}

			try {
				// Check if contentWindow is available
				if (this.currentIframe.contentWindow) {
					this.currentIframe.contentWindow.postMessage(message, '*');
				}
			} catch (error) {
				console.warn('Failed to control animation:', error);
			}
		},

		handleIframeLoad(event) {
			this.currentIframe = event.target;
			this.isIframeReady = true;

			// Only handle initialization if this is not a clip transition
			if (!this.isClipTransition) {
				// Initialize animation state
				if (this.isPlaying) {
					this.controlAnimation('play');
				} else {
					this.controlAnimation('pause');
				}

				// If we're not at the start of the clip
				if (this.clipProgress > 0) {
					this.controlAnimation('seek', this.clipProgress * 1000);
				}
			} else {
				// For clip transitions, always restart
				this.controlAnimation('restart');
				// Reset transition flag after a short delay
				setTimeout(() => {
					this.isClipTransition = false;
				}, 100);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.highlight-animation {
	position: absolute;
	top: 0;
	left: 50%;
	transform: translateX(-50%);
	width: auto;
	height: 100%;
	aspect-ratio: 16/9;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	pointer-events: none;
}

.highlight-animation__iframe {
	width: 1920px;
	height: 1080px;
	border: none;
	transform-origin: top left;
	position: absolute;
	top: 0;
	left: 0;
}

.highlight-animation__preload {
	display: none;
}
</style>
